import { useId } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { SocialPlatform, type PlatformPerformance } from '@mg/shared';
import { PLATFORM_META } from '../social/platforms';
import { cn } from '../../lib/cn';

/**
 * Followers per connected ACCOUNT, as a vertical bar chart. One bar for each account, so two
 * Pages on one platform are two bars — each labelled with its own name under its platform's icon.
 *
 * Each bar is drawn in its account's PLATFORM colour: the design spec asks for "the platform color,
 * not one blue" here, which replaced the single bar colour this chart used to have. Colour still
 * does not tell the accounts apart, and is not asked to: Facebook and LinkedIn blues are ΔE 8.4
 * apart (below the 15 floor for normal colour vision, let alone CVD), X and Threads share one
 * colour, and two Pages on one platform share everything. The name and platform icon under each
 * bar, and the title on its column, are what identify the account.
 */
const BAR_COLOUR: Record<SocialPlatform, string> = {
  [SocialPlatform.LINKEDIN]: 'bg-platform-linkedin',
  [SocialPlatform.FACEBOOK]: 'bg-platform-facebook',
  [SocialPlatform.INSTAGRAM]: 'bg-platform-instagram',
  // Black on their own sites. The ink token is near-black on a light card and near-white on a
  // dark one, where a black bar would sink into the card.
  [SocialPlatform.THREADS]: 'bg-ink',
  [SocialPlatform.X]: 'bg-ink',
  [SocialPlatform.YOUTUBE_SHORTS]: 'bg-[#FF0000]',
};

/**
 * Up to this many bars share the width they are given. Past it each bar keeps a readable width
 * (the Dashboard card is narrow) and the plot scrolls sideways instead of squeezing the names away.
 */
const MAX_FITTED_BARS = 6;
/**
 * The narrowest a bar's column gets when the plot scrolls — `min-w-[3.5rem]` below (56px at full
 * size). In rem like the classes they mirror, so the stated width still matches the columns when
 * index.css scales the UI to 80%.
 */
const BAR_MIN_REM = 3.5;
/** The space between columns — `gap-2` below. */
const BAR_GAP_REM = 0.5;

/** `null` means the platform doesn't report this metric with our tokens — NOT zero. */
export function FollowersBarChart({ rows }: { rows: PlatformPerformance[] }) {
  const titleId = useId();
  const known = rows.filter((r) => r.followers != null);
  // A y-axis needs a scale, and one built from no data would be a fiction.
  if (known.length === 0) {
    return (
      <p className="mt-4 text-sm text-ink-muted">
        No follower counts reported yet for your connected accounts.
      </p>
    );
  }

  const peak = Math.max(...known.map((r) => r.followers as number));
  const { max, ticks } = niceScale(peak);

  // Too many bars to fit: the plot and its axis get the width they need, and the wrapper scrolls.
  // The width is stated outright (not left to the columns) so the gridlines span all of it and the
  // two rows — bars and labels — stay in step.
  const scrolls = rows.length > MAX_FITTED_BARS;
  const plotWidth = scrolls
    ? { minWidth: `${rows.length * BAR_MIN_REM + (rows.length - 1) * BAR_GAP_REM}rem` }
    : undefined;
  const column = scrolls ? 'min-w-[3.5rem]' : 'min-w-0';

  return (
    <figure className="mt-4" aria-labelledby={titleId}>
      <figcaption id={titleId} className="sr-only">
        Followers by account
      </figcaption>

      <div className="flex gap-2">
        {/* Y axis: ticks sit against the gridlines they label. */}
        <div
          className="relative w-9 shrink-0 text-right text-[0.6875rem] tabular-nums text-ink-faint"
          style={{ height: PLOT_H }}
          aria-hidden
        >
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute right-0 -translate-y-1/2 leading-none"
              style={{ top: `${(1 - t / max) * 100}%` }}
            >
              {compact(t)}
            </span>
          ))}
        </div>

        <div className={cn('min-w-0 flex-1', scrolls && 'overflow-x-auto')}>
          <div className="relative" style={{ height: PLOT_H, ...plotWidth }}>
            {/* Recessive gridlines, behind the bars. */}
            {ticks.map((t) => (
              <div
                key={t}
                className={cn(
                  'absolute inset-x-0 border-t',
                  t === 0 ? 'border-line' : 'border-line-soft',
                )}
                style={{ top: `${(1 - t / max) * 100}%` }}
                aria-hidden
              />
            ))}

            <div className="absolute inset-0 flex items-end justify-around gap-2">
              {rows.map((row) => {
                const value = row.followers;
                return (
                  <div
                    // By account, not platform: two accounts on one platform are two bars.
                    key={row.connectionId}
                    className={cn('group relative flex h-full flex-1 flex-col justify-end', column)}
                    // The whole column is the hit target, not just the bar — a short bar is a
                    // tiny one, and the value is what people are reaching for.
                    title={
                      value != null
                        ? `${fullName(row)}: ${value.toLocaleString()} followers`
                        : `${fullName(row)}: not reported`
                    }
                  >
                    <span className="mb-1 block text-center text-xs font-semibold tabular-nums text-ink">
                      {value != null ? compact(value) : '—'}
                    </span>
                    {value != null ? (
                      <div
                        // Rounded top only: the bar is anchored to the baseline, so a rounded
                        // bottom would float it off the axis.
                        className={cn('w-full rounded-t', BAR_COLOUR[row.platform])}
                        style={{
                          height: `${Math.max((value / max) * 100, value > 0 ? 1.5 : 0)}%`,
                        }}
                      />
                    ) : (
                      <div className="h-1 w-full rounded-t bg-line" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* X axis: the account's name under its platform's icon, in the platform's own colour. */}
          <div className="mt-2 flex items-start justify-around gap-2" style={plotWidth}>
            {rows.map((row) => {
              const meta = PLATFORM_META[row.platform];
              return (
                <div
                  key={row.connectionId}
                  className={cn('flex flex-1 flex-col items-center gap-1 text-center', column)}
                  // The name is cut to the bar's width; this is all of it, with its platform.
                  title={fullName(row)}
                >
                  <meta.Icon
                    className={cn('h-4 w-4 shrink-0', meta.ink)}
                    aria-hidden
                  />
                  <span className="flex w-full min-w-0 items-center justify-center gap-0.5 text-[0.6875rem] text-ink-muted">
                    <span className="truncate">{row.accountName || meta.label}</span>
                    <TrendGlyph trend={row.trend} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </figure>
  );
}

/** "<account name> (<Platform>)" — or the platform alone for an account with no name of its own. */
function fullName(row: PlatformPerformance): string {
  const platform = PLATFORM_META[row.platform].label;
  return row.accountName ? `${row.accountName} (${platform})` : platform;
}

/** Engagement trend vs the prior period — kept beside the platform, not encoded in the bar. */
function TrendGlyph({ trend }: { trend: PlatformPerformance['trend'] }) {
  if (trend === 'up') return <ArrowUp className="h-3 w-3 shrink-0 text-success dark:text-success-dark" aria-label="Up" />;
  if (trend === 'down') return <ArrowDown className="h-3 w-3 shrink-0 text-red-500 dark:text-error-dark" aria-label="Down" />;
  if (trend === 'flat') return <Minus className="h-3 w-3 shrink-0 text-ink-ghost" aria-label="Flat" />;
  return null;
}

/** The plot's height: 168px at full size, in rem so it scales with the labels around it. */
const PLOT_H = '10.5rem';

const compact = (n: number): string =>
  new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);

/**
 * A rounded axis maximum and evenly spaced ticks, so the labels read 0 / 5k / 10k rather than
 * 0 / 4,133 / 8,266. Always four intervals, and never a zero-height scale.
 */
function niceScale(peak: number): { max: number; ticks: number[] } {
  const safe = peak > 0 ? peak : 1;
  const rawStep = safe / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const nice = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rawStep) ?? magnitude * 10;
  // Followers are whole people: a step below 1 would label the axis 0 / 0.3 / 0.5 / 0.8 on an
  // account with a handful of them.
  const step = Math.max(1, nice);
  const max = step * 4;
  return { max, ticks: [0, step, step * 2, step * 3, max] };
}
